import React, { useMemo } from "react";
import { PageContentBox } from "../../../common/page-content-box";
import ReportResultsTable from "../../../sod-reports/report-results/report-results-table";
import ReportResultsTabs from "../../../sod-reports/report-results/report-results-tabs";
import { AppDispatch, RootState } from "../../../../store";
import { useIntl } from "react-intl";
import { REPORT_LEVELS } from "../../../../utils/sodReports";
import {
    setReportSearchString as setSearchStringAction,
    setReportPageNumber as setPageNumberAction,
    setReportLimitNumber as setLimitNumberAction,
    setReportSortData as setSortDataAction,
    setReportFilterData as setFilterDataAction,
    setError as setErrorAction,
    setReportSelectedIds as setSelectedIdsAction,
    fetchRequestReportResult,
    fetchRequestReportResultColumns,
    setReportLevelWithColumns,
    setReportTypeWithColumns,
    setConflictLevelWithColumns,
    saveReportColumns,
    // calcRequestReport,
} from "../../../../reducers/administration/requestSlice";
import useReportReduxTable from "../../../common/hooks/useReportReduxTable";
import useReduxValue from "../../../common/hooks/useReduxValue";
import { REPORT_MODELING_CONFLICT_LEVELS } from "../../../../utils/reports-modeling-utils";
import ConflictLevelsPicker from "../../../reports-modeling/conflict-levels-picker";
import { Column, TableFilterData, TableSortData } from "../../../../types/common/table/table-types";
import { useSelector } from "react-redux";
import { ProgressDialog } from "../../../common/dialogs";
import { requestsService } from "../../../../services/requests-service";
import { reportsModelingService } from "../../../../services/reports-modeling-service";
import { useDispatch } from "react-redux";

const RequestDetailedConflictsTab = () => {
    const intl = useIntl();
    const dispatch = useDispatch<AppDispatch>();
    const tabs = useMemo(
        () => [
            {
                id: REPORT_LEVELS.OPERATIONS,
                label: intl.formatMessage({ id: "report-results.tabs.operations-level" }),
            },
            {
                id: REPORT_LEVELS.PERMISSIONS,
                label: intl.formatMessage({ id: "report-results.tabs.permissions-level" }),
            },
            {
                id: REPORT_LEVELS.CRITICAL_OPERATIONS,
                label: intl.formatMessage({ id: "report-results.tabs.critical-operations-level" }),
            },
            {
                id: REPORT_LEVELS.CRITICAL_PERMISSIONS,
                label: intl.formatMessage({ id: "report-results.tabs.critical-permissions-level" }),
            },
            {
                id: REPORT_LEVELS.CRITICAL_OBJECTS,
                label: intl.formatMessage({ id: "report-results.tabs.critical-objects-level" }),
            },
        ],
        [intl]
    );
    const [conflictLevel, setConflictLevel] = useReduxValue(
        (state: RootState) => state.administration.requests.reportState.conflictLevel,
        setConflictLevelWithColumns
    );
    // const currentRequest = useSelector((state: RootState) => state.administration.requests.currentRequest);
    // useEffect(() => {
    //     if (currentRequest?.hasRisks && !currentRequest.reportReady) {
    //         dispatch(calcRequestReport(currentRequest.requestId));
    //     }
    // }, []);

    const conflictLevels = useMemo(() => {
        //  if(isComplex){
        //     return Object.values({
        //         ROLE: "role",
        //         USERNAME: "username"
        //  })
        // }

        return Object.values(REPORT_MODELING_CONFLICT_LEVELS);
    }, []);
    const busyType = useSelector((state: RootState) => state.administration.requests.busyType);
    const requestId = useSelector((state: RootState) => state.administration.requests.currentRequest?.requestId);

    const getFilters = (params) => {
        const newParams = { ...params, report_level: reportLevel, report_type: reportType, conflict_level: conflictLevel };
        return requestsService.getBrmReportFilters(newParams, requestId);
    };

    const getColumns = (reportLevel, reportType, defaultColumns) => {
        return conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME
            ? reportsModelingService.getUserLevelModelingColumns(reportLevel, reportType, defaultColumns)
            : reportsModelingService.getRoleLevelModelingColumns(reportLevel, reportType, defaultColumns);
    };

    const handleSaveColumns = (columns, reportLevel, reportType, conflictLevel) => {
        dispatch(
            saveReportColumns({
                columns,
                reportLevel,
                reportType,
                conflictLevel,
            })
        );
    };

    const {
        rows,
        columns,
        total,
        busy,
        selectedIds,
        searchString,
        setSearchString,
        pageNumber,
        setPageNumber,
        limitNumber,
        setLimitNumber,
        filterData,
        setFilterData,
        sortData,
        setSortData,
        reportLevel,
        setReportLevel,
        reportType,
        setReportType,
    } = useReportReduxTable(
        (state: RootState) => state.administration.requests.reportState.rows,
        fetchRequestReportResult,
        (state: RootState) => state.administration.requests.reportState.columns,
        fetchRequestReportResultColumns,
        (state: RootState) => state.administration.requests.reportState.selectedIds,
        setSelectedIdsAction,
        (state: RootState) => state.administration.requests.reportState.searchString,
        setSearchStringAction,
        (state: RootState) => state.administration.requests.reportState.pageNumber,
        setPageNumberAction,
        (state: RootState) => state.administration.requests.reportState.limitNumber,
        setLimitNumberAction,
        (state: RootState) => state.administration.requests.reportState.sortData,
        setSortDataAction,
        (state: RootState) => state.administration.requests.reportState.filterData,
        setFilterDataAction,
        (state: RootState) => state.administration.requests.error,
        setErrorAction,
        (state: RootState) => state.administration.requests.reportState.total,
        (state: RootState) => state.administration.requests.busy,
        (state: RootState) => state.administration.requests.reportState.reportLevel,
        setReportLevelWithColumns,
        (state: RootState) => state.administration.requests.reportState.reportType,
        setReportTypeWithColumns
    );
    return (
        <>
            <PageContentBox>
                <ConflictLevelsPicker
                    value={conflictLevel as string}
                    conflictLevels={conflictLevels}
                    onChange={setConflictLevel as (conflictLevel: string) => void}
                />
                <ReportResultsTabs
                    tabs={tabs}
                    activeTabId={reportLevel as string}
                    onActiveTabIdChange={setReportLevel as (reportLevel: string) => void}
                />
                <ReportResultsTable
                    reportType={reportType as string}
                    reportLevel={reportLevel as string}
                    setReportType={setReportType as (string) => void}
                    rows={rows as []}
                    columns={columns as Column[]}
                    total={total as number}
                    selectedIds={selectedIds as []}
                    searchString={searchString as string}
                    setSearchString={setSearchString as (searchString: string) => void}
                    pageNumber={pageNumber as number}
                    setPageNumber={setPageNumber as (pageNumber: number) => void}
                    limitNumber={limitNumber as number}
                    setLimitNumber={setLimitNumber as (limitNumber: number) => void}
                    filterData={filterData as TableFilterData[]}
                    setFilterData={setFilterData as (filterData: TableFilterData) => void}
                    sortData={sortData as TableSortData[]}
                    setSortData={setSortData as (sortData: TableSortData) => void}
                    getFilters={getFilters}
                    getColumns={getColumns}
                    conflictLevel={conflictLevel as string}
                    saveReportColumns={handleSaveColumns}
                />
            </PageContentBox>
            <ProgressDialog busyType={busyType} open={busy} title={""} progress={""} />
        </>
    );
};

export default RequestDetailedConflictsTab;
